import React, { useState, useEffect } from "react";
import "./index.scss";
 
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Host from "../../Components/Host";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import ReplayIcon from "@mui/icons-material/Replay";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Button from "@mui/joy/Button";
import { useSelector, useDispatch } from "react-redux";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import ReCAPTCHA from "react-google-recaptcha";
import { useCreateInquiriesMutation } from "../../services/inquiries";
import { addToInquiry } from "../../Reducers/authReducer";
import HelmetComponent from "../../Components/HelmetComponent";

const TOPICS = [
  "General question",
  "Order & delivery",
  "Returns & refunds",
  "Payment",
  "Product question",
];

const PROMISES = [
  { icon: <VerifiedUserOutlinedIcon />, title: "100% Authentic", text: "Genuine products" },
  { icon: <StarBorderRoundedIcon />, title: "Premium Quality", text: "Curated with care" },
  { icon: <ReplayIcon />, title: "Easy Return", text: "Hassle-free return" },
  { icon: <LockOutlinedIcon />, title: "Secure Payments", text: "Your payments are safe" },
];

function ContactUs() {
  const { countryCode, languageCode, isInquiry } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { topic: TOPICS[0] } });

  const dispatch = useDispatch();
  const [createinquiry, createresult] = useCreateInquiriesMutation();

  const [getPhone, setPhone] = useState("");
  const [getPhoneError, setPhoneError] = useState(false);
  const [isAlready, setAlready] = useState(false);
  const recaptchaRef = React.useRef();

  async function createInquiryFrom(data) {
    const token = await recaptchaRef.current.executeAsync();
    recaptchaRef.current.reset();
    if (getPhone && isValidPhoneNumber(getPhone)) {
      setPhoneError(false);
      if (isInquiry) {
        setAlready(true);
      } else {
        createinquiry({
          name: data.name,
          email: data.email,
          // topic is added at the start of the message so the team sees it
          message: `[${data.topic}] ${data.message}`,
          phone: getPhone,
          language: languageCode,
          country: countryCode,
          captcha_token: token,
          domain: Host?.domain,
          domainEmail: Host?.email,
        });
      }
    } else {
      setPhoneError(true);
    }
  }

  useEffect(() => {
    if (createresult?.isSuccess) {
      dispatch(addToInquiry({ isInquiry: true }));
    }
  }, [createresult]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, []);

  const isSent = createresult?.isSuccess;
  const showResult = isSent || isAlready;

  return (
    <>
      <HelmetComponent title={"Contact Us"} />

      <main className="contact-page">
        {/* Title band – matches the site header */}
        <section className="contact-hero">
          <div className="contact-wrap">
            <nav className="contact-crumbs" aria-label="Breadcrumb">
              <a href="/">Home</a>
              <span aria-hidden="true">/</span>
              <span>Contact Us</span>
            </nav>
            <h1>How can we help?</h1>
            <p>Questions about an order, a product or a return? Our team is ready to help.</p>
          </div>
        </section>

        <div className="contact-wrap contact-body">
          <div className="contact-layout">
            {/* ---------- Form ---------- */}
            <section className="contact-card" aria-labelledby="contact-form-title">
              <h2 id="contact-form-title">{t("Send us your query")}</h2>
              <p className="contact-card__sub">Fill in the form and we will get back to you shortly.</p>

              {showResult ? (
                <div role="status" className={`contact-alert ${!isSent && isAlready ? "is-warn" : "is-ok"}`}>
                  <CheckCircleOutlineIcon className="contact-alert__icon" />
                  <strong>{!isSent && isAlready ? "Inquiry already received" : "Thank you, message sent"}</strong>
                  <span>
                    {!isSent && isAlready
                      ? "Your inquiry has already been received. We will get back to you shortly."
                      : "Your inquiry has been received. We will get back to you shortly."}
                  </span>
                </div>
              ) : (
                <form onSubmit={handleSubmit(createInquiryFrom)} noValidate>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    size="invisible"
                    sitekey="6LfD62EnAAAAAJ83k0BznR8m_cXInxBFjtTokspL"
                  />

                  <div className="contact-field">
                    <label htmlFor="topic">What is this about?</label>
                    <select id="topic" className="contact-input" {...register("topic")}>
                      {TOPICS.map((topic) => (
                        <option key={topic} value={topic}>
                          {topic}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="contact-row">
                    <div className="contact-field">
                      <label htmlFor="name">{t("name")}</label>
                      <input
                        type="text"
                        id="name"
                        autoComplete="name"
                        className={`contact-input ${errors.name ? "is-invalid" : ""}`}
                        aria-invalid={errors.name ? "true" : "false"}
                        {...register("name", { required: "Please enter your name" })}
                      />
                      {errors.name && <small className="contact-error">{errors.name.message}</small>}
                    </div>

                    <div className="contact-field">
                      <label htmlFor="email">{t("email")}</label>
                      <input
                        type="email"
                        id="email"
                        autoComplete="email"
                        className={`contact-input ${errors.email ? "is-invalid" : ""}`}
                        aria-invalid={errors.email ? "true" : "false"}
                        {...register("email", {
                          required: "Please enter your email",
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: "Please enter a valid email address",
                          },
                        })}
                      />
                      {errors.email && <small className="contact-error">{errors.email.message}</small>}
                    </div>
                  </div>

                  <div className="contact-field">
                    <label htmlFor="phone">{t("phone")}</label>
                    <PhoneInput
                      id="phone"
                      international
                      countryCallingCodeEditable={false}
                      defaultCountry={countryCode}
                      value={getPhone}
                      onChange={setPhone}
                      className={getPhoneError ? "is-invalid" : ""}
                    />
                    {getPhoneError && !(getPhone && isValidPhoneNumber(getPhone)) && (
                      <small className="contact-error">Please enter a valid phone number</small>
                    )}
                  </div>

                  <div className="contact-field">
                    <label htmlFor="message">{t("message")}</label>
                    <textarea
                      id="message"
                      rows={5}
                      className={`contact-input ${errors.message ? "is-invalid" : ""}`}
                      aria-invalid={errors.message ? "true" : "false"}
                      {...register("message", { required: "Please write your message" })}
                    ></textarea>
                    {errors.message && <small className="contact-error">{errors.message.message}</small>}
                  </div>

                  <Button
                    disabled={createresult.isLoading}
                    className="contact-submit"
                    endDecorator={!createresult.isLoading && <SendRoundedIcon fontSize="small" />}
                    style={{
                      background: Host?.bgColor || "#bf9a5a",
                      color: Host?.buttonColor || "#000",
                    }}
                    type="submit"
                  >
                    {createresult.isLoading ? t("loading") : t("submit")}
                  </Button>
                </form>
              )}
            </section>

            {/* ---------- Side column ---------- */}
            <aside className="contact-side">
              <div className="contact-panel">
                <h2>Contact details</h2>

                {Host?.email && (
                  <div className="contact-item">
                    <span className="contact-item__icon">
                      <MailOutlineIcon fontSize="small" />
                    </span>
                    <div>
                      <strong>Email</strong>
                      <a href={`mailto:${Host.email}`}>{Host.email}</a>
                    </div>
                  </div>
                )}

                {Host?.address && (
                  <div className="contact-item">
                    <span className="contact-item__icon">
                      <LocationOnOutlinedIcon fontSize="small" />
                    </span>
                    <div>
                      <strong>Address</strong>
                      <div className="contact-item__text" dangerouslySetInnerHTML={{ __html: Host.address }} />
                    </div>
                  </div>
                )}
              </div>

              <div className="contact-panel contact-panel--dark">
                <h2>Shop with confidence</h2>
                <ul className="contact-promises">
                  {PROMISES.map((p) => (
                    <li key={p.title}>
                      <span className="contact-promises__icon">{p.icon}</span>
                      <div>
                        <strong>{p.title}</strong>
                        <span>{p.text}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
}

export default ContactUs;